import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Button from "./button";
import { AlertTriangle, HelpCircle, AlertOctagon } from "lucide-react";

export interface DialogOptions {
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string; // If not provided, only "Aceptar" is shown
    variant?: "info" | "warning" | "danger";
}

type DialogResolver = (value: boolean) => void;

interface ActiveDialog extends DialogOptions {
    id: string;
    resolve: DialogResolver;
}

let activeDialogListener: ((dialog: ActiveDialog | null) => void) | null = null;

/**
 * Muestra un diálogo de confirmación que devuelve un Promise con la respuesta del usuario (true/false)
 */
export function confirmDialog(options: DialogOptions): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
        if (activeDialogListener) {
            activeDialogListener({
                id: Math.random().toString(36).substring(2, 9),
                ...options,
                resolve,
            });
        } else {
            console.warn("DialogContainer no está montado en la aplicación.");
            resolve(false);
        }
    });
}

export function DialogContainer() {
    const [activeDialog, setActiveDialog] = useState<ActiveDialog | null>(null);

    useEffect(() => {
        activeDialogListener = (dialog) => {
            setActiveDialog(dialog);
        };
        return () => {
            activeDialogListener = null;
        };
    }, []);

    const handleClose = (value: boolean) => {
        if (activeDialog) {
            activeDialog.resolve(value);
            setActiveDialog(null);
        }
    };

    const getVariantStyles = (variant: "info" | "warning" | "danger" = "info") => {
        switch (variant) {
            case "danger":
                return {
                    icon: <AlertOctagon className="w-8 h-8 text-rose-500 animate-bounce" />,
                    btnConfirm: "danger" as const,
                };
            case "warning":
                return {
                    icon: <AlertTriangle className="w-8 h-8 text-amber-500 animate-pulse" />,
                    btnConfirm: "primary" as const, // Or warning style if button supported it
                };
            case "info":
            default:
                return {
                    icon: <HelpCircle className="w-8 h-8 text-blue-500" />,
                    btnConfirm: "primary" as const,
                };
        }
    };

    const currentStyles = activeDialog ? getVariantStyles(activeDialog.variant) : null;

    return (
        <AnimatePresence>
            {activeDialog && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => handleClose(false)}
                        className="absolute inset-0 bg-black/40 backdrop-blur-md"
                    />

                    {/* Dialog Card */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 15 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 15 }}
                        className="relative w-full max-w-md overflow-hidden bg-white/80 dark:bg-zinc-900/90 border border-zinc-200/50 dark:border-zinc-800/50 backdrop-blur-2xl rounded-3xl p-6 shadow-2xl z-10"
                    >
                        <div className="flex gap-4 items-start">
                            <div className="p-3 bg-zinc-100 dark:bg-zinc-800/50 rounded-2xl shrink-0">
                                {currentStyles?.icon}
                            </div>
                            <div className="flex-1 space-y-1.5">
                                <h3 className="text-lg font-bold text-zinc-900 dark:text-zinc-100">
                                    {activeDialog.title}
                                </h3>
                                <p className="text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed">
                                    {activeDialog.message}
                                </p>
                            </div>
                        </div>

                        <div className="mt-6 flex justify-end gap-3">
                            {activeDialog.cancelLabel && (
                                <Button variant="secondary" onClick={() => handleClose(false)}>
                                    {activeDialog.cancelLabel}
                                </Button>
                            )}
                            <Button
                                variant={currentStyles?.btnConfirm || "primary"}
                                onClick={() => handleClose(true)}
                            >
                                {activeDialog.confirmLabel || "Aceptar"}
                            </Button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
